import { getApiAddress } from '@/core/apiConfig';
import type { UserProfileDto } from './userSettingsDtos';
import type { PreferenceDto, UserPreferences } from './userSettingsTypes';
import { parseUserPreferences } from './utils';

const USER_SETTINGS_ENDPOINT = 'user-preferences/v1/user-preferences';

const parseResponse = (response: Response, parseJson = true) => {
  const { status } = response;

  if (status == 404) {
    // if it doesnt exist, return undefined to create new one
    return undefined;
  }

  if (status < 200 || status >= 400) {
    throw new Error(status.toString());
  }

  return parseJson ? response.json() : response;
};

export const getMySettings = async () => {
  const response = await fetch(`${getApiAddress()}/${USER_SETTINGS_ENDPOINT}/me?scope=DECORATOR`, {
    credentials: 'include',
    method: 'GET',
  });

  const { status } = response;
  if (status < 200 || status >= 400) {
    throw new Error('Error while trying to fetch user preferences');
  }

  const preferences: UserPreferences = await parseUserPreferences(response);
  return preferences;
};

/**
 * Get users profiles from an array of user IDs.
 * @param userIds Array of user IDs.
 */
export const getUsersSettings = async (userIds: string[]): Promise<UserProfileDto[]> => {
  // Prevent queries with empty list.
  if (userIds.length === 0) {
    return [];
  }

  const headers = new Headers({ 'Content-Type': 'application/json' });

  try {
    const response = await fetch(
      `${getApiAddress()}/${USER_SETTINGS_ENDPOINT}/users?scope=DECORATOR`,
      {
        body: JSON.stringify(userIds),
        credentials: 'include',
        headers,
        method: 'POST',
      },
    );
    return parseResponse(response);
  } catch (error) {
    if (!error.toString().includes('Failed to fetch')) {
      console.error(error);
    }
  }
};

/**
 * Update user preferences.
 * @param preferences User preferences to update.
 */
export const updateMySettings = async (preference: PreferenceDto) => {
  const headers = new Headers({ 'Content-Type': 'application/json' });

  try {
    const response = await fetch(`${getApiAddress()}/${USER_SETTINGS_ENDPOINT}/me`, {
      body: JSON.stringify(preference),
      credentials: 'include',
      headers,
      method: 'PATCH',
    });

    return parseResponse(response, false);
  } catch (error) {
    console.error('updateMySettings - API error:', error);
  }
};

export const getUsers = async (userIds: string[]) => {
  const headers = new Headers({ 'Content-Type': 'application/json' });

  const response = await fetch(
    `${getApiAddress()}/${USER_SETTINGS_ENDPOINT}/users?scope=DECORATOR`,
    {
      credentials: 'include',
      headers,
      method: 'POST',
      body: JSON.stringify(userIds),
    },
  )
    .then(response => {
      return response.json();
    })
    .catch(error => {
      console.error('Error fetching users');
      throw error;
    });
  return response;
};

export const updateUserSettings = async (userId: string, orgId: string, settings: any) => {
  const headers = new Headers({ 'Content-Type': 'application/json' });

  try {
    const response = await fetch(
      `${getApiAddress()}/${USER_SETTINGS_ENDPOINT}/org/${orgId}/user/${userId}`,
      {
        body: JSON.stringify(settings),
        credentials: 'include',
        headers,
        method: 'PATCH',
      },
    );

    return parseResponse(response, false);
  } catch (error) {
    console.error(error);
  }

  return;
};
